<template>
  <main class="mobile-page service-screen">
    <div class="mobile-screen">
      <header class="mobile-topnav">
        <RouterLink class="mobile-icon-btn" to="/mobile/user" aria-label="返回我的">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </RouterLink>
        <span class="mobile-topnav-title">收货地址</span>
        <i class="nav-space"></i>
      </header>

      <section class="page-hero">
        <span class="mobile-eyebrow">Delivery · 配送信息</span>
        <h1>实体产品收货地址</h1>
        <p>官网订单提交后会自动保存最近使用的收货地址，便于后续下单与查看。</p>
      </section>

      <div v-if="loading" class="mobile-card loading-card" aria-live="polite">正在加载收货地址...</div>
      <div v-else-if="message" class="mobile-card message-card" role="alert">
        <p>{{ message }}</p>
        <button class="mobile-btn mobile-btn-primary" type="button" @click="loadAddresses">重新加载</button>
      </div>
      <div v-else-if="addresses.length === 0" class="mobile-card empty-card">
        <strong>暂无收货地址</strong>
        <p>完成一次官网下单后，系统会自动保存订单中的收货地址。</p>
      </div>

      <article v-else v-for="address in addresses" :key="address.id" class="mobile-card address-card">
        <div class="address-top">
          <div>
            <strong>{{ address.receiverName || '-' }}</strong>
            <span>{{ address.receiverPhone || '-' }}</span>
          </div>
          <em :class="{ default: Number(address.defaultFlag) === 1 }">
            {{ Number(address.defaultFlag) === 1 ? '默认地址' : '历史地址' }}
          </em>
        </div>
        <p>{{ fullAddress(address) }}</p>
        <small>保存时间 · {{ formatDate(address.createTime) }}</small>
        <div class="address-actions">
          <button type="button" :disabled="deletingId === address.id" @click="pendingAddress = address">
            {{ deletingId === address.id ? '正在删除' : '删除地址' }}
          </button>
        </div>
      </article>
    </div>
    <MobileTabbar active="user" />

    <Teleport to="body">
      <div v-if="pendingAddress" class="delete-address-mask" @click.self="closeDeleteConfirm">
        <section class="delete-address-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-address-title">
          <h2 id="delete-address-title">删除这个地址？</h2>
          <p>{{ pendingAddress.receiverName || '-' }} · {{ fullAddress(pendingAddress) }}</p>
          <div class="dialog-actions">
            <button type="button" :disabled="Boolean(deletingId)" @click="closeDeleteConfirm">再想想</button>
            <button type="button" :disabled="Boolean(deletingId)" @click="deleteAddress">
              {{ deletingId ? '正在删除' : '确认删除' }}
            </button>
          </div>
        </section>
      </div>
    </Teleport>
  </main>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import MobileTabbar from '@/components/mobile/MobileTabbar.vue'
import { deleteWebsiteAddress, getWebsiteAddresses } from '@/api/trade'
import { formatChinaAreaAddress } from '@/data/chinaArea'

const router = useRouter()
const addresses = ref([])
const loading = ref(false)
const message = ref('')
const pendingAddress = ref(null)
const deletingId = ref('')

async function loadAddresses() {
  if (!localStorage.getItem('dzzj_website_token')) {
    router.push({ path: '/mobile/login', query: { redirect: '/mobile/address' } })
    return
  }
  loading.value = true
  message.value = ''
  try {
    addresses.value = await getWebsiteAddresses()
  } catch (error) {
    message.value = error.message || '收货地址加载失败'
  } finally {
    loading.value = false
  }
}

function fullAddress(address) {
  return formatChinaAreaAddress(address) || '-'
}

function formatDate(value) {
  return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
}

function closeDeleteConfirm() {
  if (deletingId.value) return
  pendingAddress.value = null
}

async function deleteAddress() {
  const address = pendingAddress.value
  if (!address) return
  deletingId.value = address.id
  try {
    const result = await deleteWebsiteAddress(address.id)
    if (result === false) {
      showToast('地址已不存在，已刷新列表')
    } else {
      showToast('地址已删除')
    }
    addresses.value = addresses.value.filter(item => item.id !== address.id)
    pendingAddress.value = null
  } catch (error) {
    showToast(error.message || '地址删除失败')
  } finally {
    deletingId.value = ''
  }
}

onMounted(loadAddresses)
</script>

<style scoped>
.service-screen { padding-bottom: calc(104px + env(safe-area-inset-bottom)); }
.nav-space { width: 36px; flex: 0 0 36px; }
.page-hero { padding: 24px 2px 18px; }
.page-hero h1 { margin: 12px 0 8px; font-size: 24px; font-weight: 900; line-height: 1.25; }
.page-hero p { color: var(--mobile-muted); font-size: 12px; line-height: 1.7; }
.address-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.address-top strong { display: block; font-size: 16px; font-weight: 900; }
.address-top span { display: block; margin-top: 4px; color: var(--mobile-muted); font-size: 12px; font-variant-numeric: tabular-nums; }
.address-top em { flex: none; padding: 5px 8px; border: 1px solid var(--mobile-line); border-radius: 999px; color: var(--mobile-faint); font-size: 10.5px; font-style: normal; font-weight: 800; background: rgba(255,255,255,.03); }
.address-top em.default { color: var(--mobile-cyan); border-color: rgba(98,244,214,.3); background: var(--mobile-cyan-soft); }
.address-card p { margin-top: 13px; padding-top: 12px; border-top: 1px dashed var(--mobile-line); color: var(--mobile-ink); font-size: 12.5px; font-weight: 700; line-height: 1.7; }
.address-card small { display: block; margin-top: 8px; color: var(--mobile-faint); font-size: 10.5px; }
.address-actions { display: flex; justify-content: flex-end; margin-top: 12px; padding-top: 11px; border-top: 1px dashed var(--mobile-line); }
.address-actions button { min-height: 36px; padding: 0 14px; border: 1px solid rgba(255,156,156,.28); border-radius: 999px; color: #ff9c9c; font-size: 11.5px; font-weight: 800; background: rgba(255,156,156,.07); }
.address-actions button:active { background: rgba(255,156,156,.14); }
.address-actions button:disabled { opacity: .55; }
.loading-card, .message-card, .empty-card { text-align: center; }
.loading-card { color: var(--mobile-muted); font-size: 12.5px; }
.message-card { display: grid; gap: 12px; }
.message-card p { color: #ff9c9c; font-size: 12.5px; line-height: 1.6; }
.empty-card strong { display: block; margin-bottom: 7px; font-size: 15px; }
.empty-card p { color: var(--mobile-muted); font-size: 12px; line-height: 1.7; }
.delete-address-mask { position: fixed; inset: 0; z-index: 100; display: flex; align-items: center; justify-content: center; padding: 22px; background: rgba(1,4,10,.74); backdrop-filter: blur(6px); }
.delete-address-dialog { width: min(100%, 360px); display: grid; gap: 12px; padding: 20px 18px; border: 1px solid var(--mobile-line-strong); border-radius: 18px; background: linear-gradient(165deg, rgba(18,31,52,.98), rgba(7,15,29,.99)); box-shadow: 0 20px 52px rgba(0,0,0,.42); }
.delete-address-dialog h2 { color: var(--mobile-ink); font-size: 17px; font-weight: 900; }
.delete-address-dialog p { color: var(--mobile-muted); font-size: 12px; line-height: 1.7; word-break: break-all; }
.dialog-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 2px; }
.dialog-actions button { min-height: 44px; border-radius: 12px; font-size: 13.5px; font-weight: 800; }
.dialog-actions button:first-child { border: 1px solid var(--mobile-line-strong); color: var(--mobile-muted); background: rgba(255,255,255,.04); }
.dialog-actions button:last-child { color: #ffd9d9; background: rgba(226,90,90,.82); }
.dialog-actions button:disabled { opacity: .6; }
</style>
